import { useEffect, useId, useMemo, useRef, useState } from 'react'
import Decimal from 'decimal.js'
import { ChevronLeft, ChevronRight, TrendingDown, TrendingUp } from 'lucide-react'
import { Button, Card, MoneyValue, Skeleton } from '@shared/design-system/ui'
import { vars } from '@shared/design-system/tokens/contract.css'
import { formatMoney } from '@shared/lib/format/currency'
import type { AdminSalesSeries } from '../api/admin.api'
import { AdminChartStage, AdminChartStageTrigger } from './AdminChartStage'
import { AdminSalesChartViewMenu } from './AdminSalesChartViewMenu'
import { bucketLabel, chartBuckets, pointsThroughNow } from './admin-sales-chart-series'
import type { ChartMode } from './admin-sales-chart-series'
import * as styles from './admin-sales-chart.css'

type AdminSalesChartProps = {
  data: AdminSalesSeries | undefined
  currency: string
  periodLabel: string
  isLoading: boolean
  showComparison?: boolean
  title?: string
  caption?: string
  emptyLabel?: string
  unitLabel?: string
  live?: AdminSalesChartLive
}

type AdminSalesChartLive = {
  open: boolean
  series?: AdminSalesSeries
  dataUpdatedAt: number
  isError: boolean
  onOpen: () => void
  onClose: () => void
  onRetry: () => void
}

/**
 * Vendas pagas ao longo do período, no mesmo recorte dos cartões acima. Segue o gráfico do
 * painel do vendedor: acumulado ou por período, com comparação contra a janela anterior.
 * O botão de tela cheia reabre o mesmo gráfico num palco para telão.
 */
export function AdminSalesChart(props: AdminSalesChartProps) {
  const [localStageOpen, setLocalStageOpen] = useState(false)
  const stageOpen = props.live?.open ?? localStageOpen
  const [now, setNow] = useState(() => Date.now())
  const title = props.title ?? 'Volume de cobranças pagas'
  const granularity = props.data?.granularity ?? 'day'
  useEffect(() => {
    if (granularity !== 'hour') return
    const timer = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [granularity])
  // A visão fica aqui para a tela cheia abrir no mesmo recorte que está no cartão.
  const [mode, setMode] = useState<ChartMode>('cumulative')
  const [selected, setSelected] = useState<number | null>(null)
  const [modeGranularity, setModeGranularity] = useState(granularity)
  if (modeGranularity !== granularity) {
    setModeGranularity(granularity)
    setMode('cumulative')
    setSelected(null)
  }
  const view = {
    mode,
    selected,
    onSelect: setSelected,
    onModeChange: (next: ChartMode) => {
      setMode(next)
      setSelected(null)
    },
  }
  const openStage = () => {
    if (props.live) props.live.onOpen()
    else setLocalStageOpen(true)
  }
  const closeStage = () => {
    if (props.live) props.live.onClose()
    else setLocalStageOpen(false)
  }

  return (
    <>
      <AdminSalesChartCard {...props} {...view} now={now} onMaximize={openStage} />
      <AdminChartStage
        open={stageOpen}
        onClose={closeStage}
        title={title}
        subtitle={`${props.periodLabel} · ${props.currency}`}
        liveMode={Boolean(props.live)}
        testId={props.live ? 'admin-dashboard-live-stage' : undefined}
      >
        <AdminSalesChartCard
          {...props}
          {...view}
          data={props.live?.series ?? props.data}
          now={now}
          stage
        />
      </AdminChartStage>
    </>
  )
}

function AdminSalesChartCard({
  data,
  currency,
  periodLabel,
  isLoading,
  showComparison = true,
  title = 'Volume de cobranças pagas',
  caption = 'Cobranças criadas no período que estão pagas atualmente, antes das taxas',
  emptyLabel,
  unitLabel = 'cobrança',
  live,
  mode,
  selected,
  onSelect,
  onModeChange,
  onMaximize,
  stage = false,
  now,
}: AdminSalesChartProps & {
  mode: ChartMode
  selected: number | null
  onSelect: (index: number | null) => void
  onModeChange: (mode: ChartMode) => void
  onMaximize?: () => void
  stage?: boolean
  now: number
}) {
  const id = useId().replaceAll(':', '')
  const frameRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const [width, setWidth] = useState(0)
  const [frameHeight, setFrameHeight] = useState(0)
  const granularity = data?.granularity ?? 'day'
  const points = useMemo(() => {
    const series = data?.series.find((item) => item.currency === currency)?.points ?? []
    return pointsThroughNow(series, granularity, now)
  }, [data, currency, granularity, now])
  const buckets = useMemo(() => chartBuckets(points, mode), [points, mode])
  const chartLabel =
    mode === 'cumulative'
      ? 'Valor acumulado no período'
      : granularity === 'hour'
        ? 'Valor por hora'
        : granularity === 'month'
          ? 'Valor por mês'
          : 'Valor por dia'
  const amount = points.reduce((sum, point) => sum.plus(point.amount.amount), new Decimal(0))
  const previous = points.reduce(
    (sum, point) => sum.plus(point.previousAmount.amount),
    new Decimal(0),
  )
  const delta = previous.isZero() ? null : amount.minus(previous).div(previous).times(100)
  const count = points.reduce((sum, point) => sum + point.count, 0)
  const index = Math.min(
    selected ?? Math.max(0, buckets.length - 1),
    Math.max(0, buckets.length - 1),
  )
  const active = buckets[index]
  const plotWidth = width > 0 ? width : 640
  const height = stage
    ? Math.max(280, frameHeight > 0 ? frameHeight : 480)
    : plotWidth < 480
      ? 240
      : 280
  const compact = plotWidth < 480
  const left = compact ? 8 : stage ? 104 : 72
  const right = plotWidth - 16
  const bottom = height - (stage ? 52 : 38)
  // y da moeda é a base do texto. No palco a fonte do eixo vai a 18px; com y=12
  // o topo de "BRL" saía do viewBox e aparecia cortado.
  const currencyLabelY = stage ? 22 : 14
  const top = stage ? 52 : 24
  const maximum = Decimal.max(1, ...buckets.map((point) => point.amount))
    .times(1.15)
    .toNumber()
  const positions = buckets.map((point, i) => ({
    x:
      mode === 'daily'
        ? left + ((i + 0.5) * (right - left)) / buckets.length
        : buckets.length === 1
          ? (left + right) / 2
          : left + (i * (right - left)) / (buckets.length - 1),
    y: bottom - (point.amount.toNumber() / maximum) * (bottom - top),
  }))
  let line = positions.length ? `M ${positions[0]!.x} ${positions[0]!.y}` : ''
  for (let i = 1; i < positions.length; i++) {
    const a = positions[i - 1]!,
      b = positions[i]!,
      middle = (a.x + b.x) / 2
    line += ` C ${middle} ${a.y} ${middle} ${b.y} ${b.x} ${b.y}`
  }
  const tickCount = plotWidth < 270 ? 2 : plotWidth < 480 ? 3 : stage ? 12 : 6
  const ticks = Array.from(
    new Set(
      Array.from({ length: tickCount }, (_, i) =>
        Math.round((i * (buckets.length - 1)) / (tickCount - 1)),
      ),
    ),
  )
  const hasSales = !amount.isZero()
  const liveUpdatedAt = live?.dataUpdatedAt
    ? new Date(live.dataUpdatedAt).toLocaleTimeString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
      })
    : null

  // O desenho fica invisível até medir a largura. Em Finanças a série de hoje
  // pode chegar com hasSales já verdadeiro enquanto o de ontem ainda carrega:
  // o frame só entra no DOM quando isLoading vira falso, então a medida tem
  // que rodar de novo nesse momento.
  useEffect(() => {
    const element = frameRef.current
    if (!element) return
    const apply = (nextWidth: number, nextHeight: number) => {
      if (nextWidth > 0) setWidth(Math.round(nextWidth))
      if (nextHeight > 0) setFrameHeight(Math.round(nextHeight))
    }
    const box = element.getBoundingClientRect()
    apply(box.width, box.height)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      apply(rect?.width ?? 0, rect?.height ?? 0)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [hasSales, isLoading])

  function selectAt(clientX: number) {
    const x = clientX - (svgRef.current?.getBoundingClientRect().left ?? 0)
    onSelect(
      Math.max(
        0,
        Math.min(
          buckets.length - 1,
          mode === 'daily'
            ? Math.floor(((x - left) / (right - left)) * buckets.length)
            : Math.round(((x - left) / (right - left)) * (buckets.length - 1)),
        ),
      ),
    )
  }

  if (isLoading) {
    return (
      <Card padding="lg" className={styles.hero}>
        <Skeleton className={styles.loading} />
      </Card>
    )
  }

  return (
    <Card padding="lg" className={stage ? `${styles.hero} ${styles.heroStage}` : styles.hero}>
      {onMaximize ? <AdminChartStageTrigger onOpen={onMaximize} /> : null}
      <AdminSalesChartViewMenu mode={mode} onChange={onModeChange} />
      <div className={onMaximize ? `${styles.header} ${styles.headerWithStage}` : styles.header}>
        <div className={styles.copy}>
          <h2 className={styles.title}>{title}</h2>
          <MoneyValue
            amount={amount.toFixed(3)}
            currency={currency}
            size="display"
            className={styles.total}
          />
          <span className={styles.caption}>
            {periodLabel} · {currency} · {count.toLocaleString('pt-BR')}{' '}
            {count === 1 ? unitLabel : `${unitLabel}s`}
          </span>
        </div>
        <div className={styles.comparison}>
          {!showComparison ? (
            <span className={styles.caption}>
              {granularity === 'hour'
                ? 'Dia em andamento · gráfico hora a hora'
                : 'Dia em andamento · gráfico diário'}
            </span>
          ) : delta === null ? (
            <span className={styles.caption}>Sem base no período anterior</span>
          ) : (
            <span className={delta.isNegative() ? styles.negative : styles.positive}>
              {delta.isNegative() ? (
                <TrendingDown size={16} aria-hidden />
              ) : (
                <TrendingUp size={16} aria-hidden />
              )}{' '}
              {delta.isPositive() ? '+' : ''}
              {delta.toFixed(1).replace('.', ',')}%
            </span>
          )}
          {showComparison && delta !== null ? (
            <span className={styles.caption}>vs. período anterior equivalente</span>
          ) : null}
        </div>
      </div>
      {stage && live ? (
        <div className={styles.liveStatus} role="status" data-testid="admin-dashboard-live-status">
          Ao vivo · {liveUpdatedAt ? `atualizado às ${liveUpdatedAt}` : 'atualizando...'}
        </div>
      ) : null}
      {stage && live?.isError ? (
        <div className={styles.liveError} role="alert">
          <span>Atualização interrompida. Exibindo os valores da última leitura disponível.</span>
          <Button
            variant="text"
            data-testid="admin-dashboard-live-retry-btn"
            onClick={live.onRetry}
          >
            Tentar novamente
          </Button>
        </div>
      ) : null}
      {!hasSales && granularity !== 'hour' ? (
        <div className={styles.empty}>
          {emptyLabel ?? `Nenhuma venda paga em ${currency} neste período.`}
        </div>
      ) : (
        <div
          ref={frameRef}
          className={stage ? `${styles.plotFrame} ${styles.plotFrameStage}` : styles.plotFrame}
        >
          <svg
            ref={svgRef}
            className={stage ? `${styles.plot} ${styles.plotStage}` : styles.plot}
            data-measured={width > 0}
            viewBox={`0 0 ${plotWidth} ${height}`}
            role="img"
            aria-label={`${chartLabel} em ${currency}`}
            onPointerMove={(event) => {
              if (event.pointerType === 'mouse') selectAt(event.clientX)
            }}
            onClick={(event) => selectAt(event.clientX)}
          >
            <title>{chartLabel}</title>
            <desc>
              Cobranças pagas no recorte selecionado. Use os botões anterior e próximo para
              consultar cada valor.
            </desc>
            <defs>
              <linearGradient id={`${id}-area`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={vars.color.accent} stopOpacity={0.2} />
                <stop offset="100%" stopColor={vars.color.accent} stopOpacity={0.01} />
              </linearGradient>
            </defs>
            {!compact ? (
              <text x={0} y={currencyLabelY} className={styles.axis}>
                {currency}
              </text>
            ) : null}
            {[0, 1, 2, 3].map((i) => (
              <g key={i}>
                <line
                  x1={left}
                  x2={right}
                  y1={bottom - (i * (bottom - top)) / 3}
                  y2={bottom - (i * (bottom - top)) / 3}
                  stroke={vars.color.border}
                  strokeWidth={0.7}
                />
                {!compact ? (
                  <text
                    x={left - 10}
                    y={bottom - (i * (bottom - top)) / 3 + 4}
                    textAnchor="end"
                    className={styles.axis}
                  >
                    {new Intl.NumberFormat('pt-BR', {
                      notation: 'compact',
                      maximumFractionDigits: 1,
                    }).format((maximum * i) / 3)}
                  </text>
                ) : null}
              </g>
            ))}
            {mode === 'cumulative' ? (
              <>
                <path
                  d={`${line} L ${positions[positions.length - 1]?.x} ${bottom} L ${positions[0]?.x} ${bottom} Z`}
                  fill={`url(#${id}-area)`}
                />
                <path
                  d={line}
                  fill="none"
                  stroke={vars.color.accent}
                  strokeWidth={stage ? 4 : 2.5}
                  strokeLinecap="round"
                />
              </>
            ) : (
              positions.map((point, i) => {
                const barWidth = ((right - left) / buckets.length) * 0.68
                return (
                  <rect
                    key={buckets[i]!.label}
                    x={point.x - barWidth / 2}
                    y={point.y}
                    width={barWidth}
                    height={bottom - point.y}
                    rx={Math.min(7, barWidth / 2, (bottom - point.y) / 2)}
                    fill={vars.color.accent}
                    opacity={i === index ? 1 : 0.42}
                    data-daily-bar="true"
                  />
                )
              })
            )}
            {ticks.map(
              (i, tick) =>
                buckets[i] && (
                  <text
                    key={i}
                    x={positions[i]!.x}
                    y={height - (stage ? 16 : 10)}
                    textAnchor={tick === 0 ? 'start' : tick === ticks.length - 1 ? 'end' : 'middle'}
                    className={styles.axis}
                  >
                    {bucketLabel(buckets[i].label, granularity)}
                  </text>
                ),
            )}
            {positions[index] && mode === 'cumulative' ? (
              <g>
                <line
                  x1={positions[index].x}
                  x2={positions[index].x}
                  y1={top}
                  y2={bottom}
                  stroke={vars.color.textTertiary}
                  opacity={0.5}
                />
                <circle
                  cx={positions[index].x}
                  cy={positions[index].y}
                  r={stage ? 8 : 5}
                  fill={vars.color.surface}
                  stroke={vars.color.accent}
                  strokeWidth={stage ? 4 : 2.5}
                />
              </g>
            ) : null}
          </svg>
        </div>
      )}
      <div className={styles.footer}>
        <div className={styles.copy}>
          <span className={styles.caption}>{chartLabel}</span>
          <span className={styles.caption}>{caption}</span>
        </div>
        {active && hasSales ? (
          <div className={styles.detail}>
            <Button
              variant="text"
              size="sm"
              className={styles.navButton}
              aria-label="Ver intervalo anterior"
              disabled={index === 0}
              onClick={() => onSelect(index - 1)}
            >
              <ChevronLeft size={16} />
            </Button>
            <div className={styles.pointValue}>
              <span className={styles.caption}>
                {mode === 'cumulative' ? 'Acumulado até ' : ''}
                {bucketLabel(active.through, granularity)}
              </span>
              <span>{formatMoney(active.amount.toFixed(3), currency)}</span>
            </div>
            <Button
              variant="text"
              size="sm"
              className={styles.navButton}
              aria-label="Ver próximo intervalo"
              disabled={index === buckets.length - 1}
              onClick={() => onSelect(index + 1)}
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  )
}
